/**
 * Post-ELK symmetrization helpers for branching layouts.
 *
 * ⚠️  HERE BE DRAGONS ⚠️
 *
 * This file contains the post-layout symmetry correction pipeline that runs
 * after ELK computes initial node positions. ELK's layered algorithm doesn't
 * natively produce visually balanced branch lanes, so this module:
 *
 *   - Computes per-branch subgraph widths and horizontal lane centers
 *   - Shifts branch path nodes into symmetric horizontal lanes
 *   - Vertically centers branches and enforces minimum spacing below split nodes
 *   - Repositions join/convergence targets below the deepest branch
 *   - Cascades Y-delta adjustments to downstream nodes
 *   - Generates custom edge sections (split edges + convergence edges)
 *   - Updates ELK port positions to match repositioned nodes
 *
 * These functions are called exclusively by elk-layout.ts as part of the
 * symmetrizeSplitLayout pipeline. They are not intended to be used directly
 * by other modules.
 *
 * WHEN TO MODIFY THIS FILE:
 *   - Fixing visual symmetry bugs in branching layouts
 *   - Adjusting branch lane spacing or edge routing geometry
 *   - Supporting new branching node types with different layout needs
 *
 * WHEN NOT TO MODIFY THIS FILE:
 *   - Changing ELK algorithm options → update layout-config.ts
 *   - Changing how the ELK graph is constructed → update elk-layout.ts
 *   - Changing edge/node rendering → update the edge-types/ or node-types/ components
 */
import { LAYOUT_CONSTANTS, SPLIT_LAYOUT_CONSTANTS } from './elk-config'
import { NodePosition, PortPosition, ElkEdgeSection } from './elk-types'
import { JourneyNode, JourneyEdge, BranchEdgeData } from '../types/journey-nodes'
import { isBranchingNode, getBranchingNodeData } from '../utils/graph/traversal'
import { getNodeOutStepId } from '../utils/graph/traversal'

// region Interfaces

/**
 * Information about a single branch of a branching node.
 *
 * `spineNodeIds` — the linear chain of nodes along the branch's main path
 *   (branching node → out_step_id → ... → join_step_id). When a spine node
 *   is itself a branching node, the walk jumps through its join_step_id.
 *   Used for: convergence edge lookup (last spine node), first-node targeting.
 *
 * `scopeNodeIds` — ALL nodes owned by this branch, including spine nodes
 *   plus every node inside nested branching scopes. Used for: bounding box
 *   computation, shifting, vertical centering, region membership checks.
 */
export interface BranchInfo {
    variantIdx: number
    variant: { id: string; name: string; percentage: number; out_step_id: string | number | null }
    spineNodeIds: string[]
    scopeNodeIds: string[]
}

export interface JoinRepositionResult {
    yDelta: number
    newJoinY: number
}

export interface ConvergeContext {
    baseConvergeY: number
    joinTopCenterX: number
    joinTopY: number
    splitBottomY: number
}

/**
 * Pre-computed context for a single branching node's symmetrize operations.
 * Computed once and shared between positioning (pass 1) and edge routing (pass 2)
 * to eliminate duplicated work and prevent drift between the two passes.
 */
export interface SplitContext {
    splitNode: JourneyNode
    splitPos: NodePosition
    splitWidth: number
    splitHeight: number
    splitCenterX: number
    joinStepId: string
    branches: BranchInfo[]
    nodeMap: Map<string, JourneyNode>
    variantShaped: BranchInfo['variant'][]
}

// endregion

// region Step 0: Build SplitContext

/**
 * Build the shared SplitContext for a branching node.
 * Returns null if the node is not a valid branching node with ≥2 branches.
 */
export function buildSplitContext(
    splitNode: JourneyNode,
    nodes: JourneyNode[],
    positions: Map<string, NodePosition>,
): SplitContext | null {
    if (!isBranchingNode(splitNode)) return null
    const branchingData = getBranchingNodeData(splitNode)
    if (!branchingData) return null
    const joinStepId = branchingData.join_step_id ? String(branchingData.join_step_id) : null
    if (!joinStepId || branchingData.branches.length < 2) return null
    const splitPos = positions.get(splitNode.id)
    if (!splitPos) return null

    const splitWidth = splitNode.measured?.width ?? LAYOUT_CONSTANTS.MIN_NODE_WIDTH
    const splitHeight = splitNode.measured?.height ?? LAYOUT_CONSTANTS.MIN_NODE_HEIGHT
    const splitCenterX = splitPos.x + splitWidth / 2
    const nodeMap = new Map(nodes.map(n => [n.id, n]))

    const variantShaped = branchingData.branches.map(b => ({
        id: b.key,
        name: b.label,
        percentage: b.kind === 'variant' ? b.percentage : 0,
        out_step_id: b.out_step_id,
    }))

    const branches = collectBranchSubgraphs(variantShaped, nodeMap, joinStepId)
    if (branches.length < 2) return null

    return {
        splitNode,
        splitPos,
        splitWidth,
        splitHeight,
        splitCenterX,
        joinStepId,
        branches,
        nodeMap,
        variantShaped,
    }
}

// endregion

// region Step 1: Collect branch subgraphs

/**
 * Recursively collect all nodes within a nested branching node's scope.
 * Walks each branch from its out_step_id up to (but excluding) the nested join target.
 */
function collectNestedScopeNodes(
    branchingNode: JourneyNode,
    nodeMap: Map<string, JourneyNode>,
): string[] {
    const nestedData = getBranchingNodeData(branchingNode)
    if (!nestedData) return []

    const nestedJoinId = nestedData.join_step_id != null ? String(nestedData.join_step_id) : null
    const result: string[] = []
    const visited = new Set<string>()

    for (const branch of nestedData.branches) {
        const outId = branch.out_step_id ? String(branch.out_step_id) : null
        if (!outId || outId === nestedJoinId) continue

        let currentId: string | null = outId
        while (currentId && currentId !== nestedJoinId && !visited.has(currentId)) {
            visited.add(currentId)
            result.push(currentId)
            const currentNode = nodeMap.get(currentId)
            if (!currentNode) break

            if (isBranchingNode(currentNode)) {
                result.push(...collectNestedScopeNodes(currentNode, nodeMap))
                const deepData = getBranchingNodeData(currentNode)
                if (deepData && deepData.join_step_id != null) {
                    currentId = String(deepData.join_step_id)
                    continue
                }
                break
            }

            const nextId = getNodeOutStepId(currentNode)
            currentId = nextId
        }
    }

    return result
}

/**
 * Walk each variant path to collect intermediate nodes between split and join.
 * Returns one BranchInfo per variant, in variant order.
 *
 * Produces two node lists per branch:
 *   - `spineNodeIds`: the linear chain (branching nodes are included but their
 *     internal nodes are not; the walk jumps through join_step_id)
 *   - `scopeNodeIds`: spine nodes PLUS all nodes inside nested branching scopes
 */
export function collectBranchSubgraphs(
    variants: BranchInfo['variant'][],
    nodeMap: Map<string, JourneyNode>,
    joinStepId: string,
): BranchInfo[] {
    const branches: BranchInfo[] = []

    for (let vi = 0; vi < variants.length; vi++) {
        const variant = variants[vi]
        const outId = variant.out_step_id ? String(variant.out_step_id) : null

        if (!outId || outId === joinStepId) {
            branches.push({ variantIdx: vi, variant, spineNodeIds: [], scopeNodeIds: [] })
            continue
        }

        const spineNodeIds: string[] = []
        const scopeNodeIds: string[] = []
        let currentId: string | null = outId
        while (currentId && currentId !== joinStepId) {
            spineNodeIds.push(currentId)
            scopeNodeIds.push(currentId)
            const currentNode = nodeMap.get(currentId)
            if (!currentNode) break

            if (isBranchingNode(currentNode)) {
                const nestedData = getBranchingNodeData(currentNode)
                if (nestedData && nestedData.join_step_id != null) {
                    const nestedScope = collectNestedScopeNodes(currentNode, nodeMap)
                    scopeNodeIds.push(...nestedScope)
                    currentId = String(nestedData.join_step_id)
                    continue
                }
                break
            }

            currentId = getNodeOutStepId(currentNode)
        }
        branches.push({ variantIdx: vi, variant, spineNodeIds, scopeNodeIds })
    }

    return branches
}

// endregion

// region Step 2: Compute branch widths

/**
 * Compute each branch's bounding-box half-width from scopeNodeIds.
 * Empty branches return MIN_NODE_WIDTH / 2.
 * Non-empty branches return (maxX - minX) / 2 of their bounding box.
 *
 * For branches containing nested branching nodes, the width also accounts
 * for the lane spread of those nested nodes (even when their branches are
 * empty and have no scope nodes). This prevents adjacent branches from
 * different conditions from overlapping in the visual space occupied by
 * empty-branch edges and labels.
 */
export function computeBranchWidths(
    branches: BranchInfo[],
    nodeMap: Map<string, JourneyNode>,
    positions: Map<string, NodePosition>,
): number[] {
    const branchHalfWidths: number[] = []

    for (const branch of branches) {
        if (branch.scopeNodeIds.length === 0) {
            branchHalfWidths.push(LAYOUT_CONSTANTS.MIN_NODE_WIDTH / 2)
            continue
        }

        // Standard bounding box from node positions.
        let minX = Infinity
        let maxX = -Infinity
        for (const nodeId of branch.scopeNodeIds) {
            const node = nodeMap.get(nodeId)
            const w = node?.measured?.width ?? LAYOUT_CONSTANTS.MIN_NODE_WIDTH
            const pos = positions.get(nodeId)
            if (pos) {
                minX = Math.min(minX, pos.x)
                maxX = Math.max(maxX, pos.x + w)
            }
        }

        // For nested branching nodes, ensure the width accounts for their
        // full visual extent including edge lane paths. Inner splits are
        // positioned first (deepest-first pass), so we can recompute their
        // lane centers to get the actual visual span. This is necessary
        // because empty branches have no scope nodes but their edge paths
        // fan out to lane positions that extend beyond node bounding boxes.
        for (const nodeId of branch.scopeNodeIds) {
            const node = nodeMap.get(nodeId)
            if (!node || !isBranchingNode(node)) continue
            const brData = getBranchingNodeData(node)
            if (!brData || brData.branches.length < 2) continue

            const nodePos = positions.get(nodeId)
            if (!nodePos) continue
            const nodeW = node.measured?.width ?? LAYOUT_CONSTANTS.MIN_NODE_WIDTH
            const nodeCenterX = nodePos.x + nodeW / 2

            // Recompute the nested split's lane centers using the same logic
            // that symmetrizeSingleSplitPositioning used. Since the inner pass
            // already ran, the nested scope nodes are at their final positions.
            const nestedJoinId = brData.join_step_id != null ? String(brData.join_step_id) : null
            const nestedVariants = brData.branches.map(b => ({
                id: b.key,
                name: b.label,
                percentage: b.kind === 'variant' ? b.percentage : 0,
                out_step_id: b.out_step_id,
            }))
            const nestedBranches = nestedJoinId
                ? collectBranchSubgraphs(nestedVariants, nodeMap, nestedJoinId)
                : []

            const nestedHalfWidths = computeBranchWidths(nestedBranches, nodeMap, positions)
            const nestedAnyHasNodes = nestedBranches.some(nb => nb.scopeNodeIds.length > 0)
            const nestedLaneCenters = computeLaneCenters(
                nestedBranches.length, nodeCenterX, nodeW,
                nestedAnyHasNodes, nestedHalfWidths,
            )

            // Expand bounding box to cover all nested lane centers
            for (const lc of nestedLaneCenters) {
                minX = Math.min(minX, lc - LAYOUT_CONSTANTS.MIN_NODE_WIDTH / 2)
                maxX = Math.max(maxX, lc + LAYOUT_CONSTANTS.MIN_NODE_WIDTH / 2)
            }
        }

        const halfW = minX < Infinity ? (maxX - minX) / 2 : LAYOUT_CONSTANTS.MIN_NODE_WIDTH / 2
        branchHalfWidths.push(halfW)
    }

    return branchHalfWidths
}

// endregion

// region Step 3: Compute lane centers

/**
 * Compute symmetric lane center X-coordinates around splitCenterX.
 *
 * For 2 empty variants, uses a compact gap (splitWidth * 0.6).
 * For branches with content, computes adaptive spacing based on actual
 * branch widths so that nested branching nodes don't overlap.
 * The minimum gap between adjacent lanes is LANE_OFFSET.
 */
export function computeLaneCenters(
    branchCount: number,
    splitCenterX: number,
    splitWidth: number,
    anyBranchHasNodes: boolean,
    branchHalfWidths?: number[],
): number[] {
    const { LANE_OFFSET } = SPLIT_LAYOUT_CONSTANTS
    const MIN_GAP = 40

    if (!anyBranchHasNodes && branchCount <= 2) {
        const gap = splitWidth * 0.6
        const laneCenters: number[] = []
        for (let i = 0; i < branchCount; i++) {
            const offset = (i - (branchCount - 1) / 2) * gap
            laneCenters.push(splitCenterX + offset)
        }
        return laneCenters
    }

    const halfWidths = branchHalfWidths && branchHalfWidths.length === branchCount
        ? branchHalfWidths
        : Array(branchCount).fill(LAYOUT_CONSTANTS.MIN_NODE_WIDTH / 2)

    const gaps: number[] = []
    for (let i = 0; i < branchCount - 1; i++) {
        const contentGap = halfWidths[i] + halfWidths[i + 1] + MIN_GAP
        gaps.push(Math.max(LANE_OFFSET, contentGap))
    }

    const laneCenters: number[] = [0]
    for (let i = 0; i < gaps.length; i++) {
        laneCenters.push(laneCenters[i] + gaps[i])
    }

    const totalSpan = laneCenters[laneCenters.length - 1] - laneCenters[0]
    const leftEdge = splitCenterX - totalSpan / 2
    for (let i = 0; i < laneCenters.length; i++) {
        laneCenters[i] = leftEdge + laneCenters[i]
    }

    return laneCenters
}

// endregion

// region Step 4: Shift branch nodes to lane centers

/**
 * Horizontally shift each branch's scope nodes so the branch's bounding-box
 * center aligns with its assigned lane center. Uses a delta-based shift so
 * that the relative positioning of nodes within a branch (including already-
 * positioned nested branching internals) is preserved.
 *
 * Y positions are unchanged. Mutates `positions` in place.
 */
export function shiftBranchesToLanes(
    branches: BranchInfo[],
    laneCenters: number[],
    nodeMap: Map<string, JourneyNode>,
    positions: Map<string, NodePosition>,
): void {
    for (let bi = 0; bi < branches.length; bi++) {
        const branch = branches[bi]
        const desiredCenterX = laneCenters[bi]

        if (branch.scopeNodeIds.length === 0) continue

        // Compute current bounding-box center of this branch's scope nodes.
        let minX = Infinity
        let maxX = -Infinity
        for (const nodeId of branch.scopeNodeIds) {
            const node = nodeMap.get(nodeId)
            const w = node?.measured?.width ?? LAYOUT_CONSTANTS.MIN_NODE_WIDTH
            const pos = positions.get(nodeId)
            if (pos) {
                minX = Math.min(minX, pos.x)
                maxX = Math.max(maxX, pos.x + w)
            }
        }

        if (minX >= Infinity) continue

        const currentCenterX = (minX + maxX) / 2
        const deltaX = desiredCenterX - currentCenterX

        if (Math.abs(deltaX) < 0.5) continue

        // Shift all scope nodes by the same delta, preserving internal layout.
        for (const nodeId of branch.scopeNodeIds) {
            const pos = positions.get(nodeId)
            if (pos) {
                positions.set(nodeId, { x: pos.x + deltaX, y: pos.y })
            }
        }

        // Align spine nodes to the lane center so linear chains have straight
        // vertical edges. This includes branching nodes — their internal
        // sub-lane layout was already computed in an earlier (deeper-first) pass,
        // and the bounding-box shift above moved the entire subtree as a rigid
        // block. Centering the branching node itself ensures it sits on the
        // same vertical line as its parent chain.
        for (const nodeId of branch.spineNodeIds) {
            const node = nodeMap.get(nodeId)
            if (!node) continue
            const w = node.measured?.width ?? LAYOUT_CONSTANTS.MIN_NODE_WIDTH
            const pos = positions.get(nodeId)
            if (pos) {
                positions.set(nodeId, { x: desiredCenterX - w / 2, y: pos.y })
            }
        }
    }
}

// endregion

// region Step 4b: Vertically center shorter branches

/**
 * Top-align all non-empty branches so they start at the same Y position.
 * Uses scopeNodeIds for span computation so nested nodes are included.
 *
 * In a top-down workflow, branches should begin at the same vertical level
 * below the split node. This prevents edge crossings that occur when
 * branches are vertically centered (which pushes shorter branches down).
 *
 * The target top Y is the minimum top across all non-empty branches
 * (i.e., the highest branch determines the starting level).
 *
 * Empty branches are not moved. Mutates `positions` in place.
 */
export function verticallyCenterBranches(
    branches: BranchInfo[],
    nodeMap: Map<string, JourneyNode>,
    positions: Map<string, NodePosition>,
): void {
    if (!branches.some(b => b.scopeNodeIds.length > 0)) return

    const branchSpans = branches.map(branch => {
        if (branch.scopeNodeIds.length === 0) return { top: Infinity, bottom: -Infinity, height: 0 }
        let top = Infinity
        let bottom = -Infinity
        for (const nodeId of branch.scopeNodeIds) {
            const pos = positions.get(nodeId)
            const node = nodeMap.get(nodeId)
            const h = node?.measured?.height ?? LAYOUT_CONSTANTS.MIN_NODE_HEIGHT
            if (pos) {
                top = Math.min(top, pos.y)
                bottom = Math.max(bottom, pos.y + h)
            }
        }
        return { top, bottom, height: bottom - top }
    })

    // Find the tallest branch and center shorter branches around its midpoint.
    const nonEmpty = branchSpans.filter(s => s.height > 0)
    if (nonEmpty.length === 0) return

    const tallest = nonEmpty.reduce((a, b) => a.height >= b.height ? a : b)
    const tallestMidY = tallest.top + tallest.height / 2

    for (let bi = 0; bi < branches.length; bi++) {
        const span = branchSpans[bi]
        if (span.height <= 0) continue

        const branchMidY = span.top + span.height / 2
        const yOffset = tallestMidY - branchMidY

        if (Math.abs(yOffset) < 0.5) continue

        for (const nodeId of branches[bi].scopeNodeIds) {
            const pos = positions.get(nodeId)
            if (pos) {
                positions.set(nodeId, { x: pos.x, y: pos.y + yOffset })
            }
        }
    }
}

// endregion

// region Step 4c: Enforce branches below split node

/**
 * Ensure all branch scope nodes are positioned below the split node.
 *
 * When a node is inserted between a split and an existing branching node,
 * ELK may recalculate the layout such that the branching node's branches
 * end up at the same Y or above the branching node itself. This function
 * detects that case and pushes all branch nodes down so the topmost branch
 * node starts at splitBottom + BEND_GAP.
 *
 * Mutates `positions` in place.
 */
export function enforceBranchesBelowSplit(
    branches: BranchInfo[],
    positions: Map<string, NodePosition>,
    splitPos: NodePosition,
    splitHeight: number,
): void {
    const { BEND_GAP, LABEL_VERTICAL_OFFSET } = SPLIT_LAYOUT_CONSTANTS
    // Branch nodes must be far enough below the split that the edge has room
    // for: port → BEND_GAP vertical drop → horizontal bend → label → vertical
    // drop into the node. Using 2× BEND_GAP + label offset ensures a clean
    // vertical segment after the bend before reaching the first node.
    const targetBranchY = splitPos.y + splitHeight + BEND_GAP * 2 + LABEL_VERTICAL_OFFSET

    // Find the topmost branch scope node.
    let topY = Infinity
    for (const branch of branches) {
        for (const nodeId of branch.scopeNodeIds) {
            const pos = positions.get(nodeId)
            if (pos && pos.y < topY) {
                topY = pos.y
            }
        }
    }

    if (topY === Infinity) return

    // Snap the topmost branch node to the target Y — push down if too close
    // to the split, pull up if ELK placed branches excessively far away.
    const delta = targetBranchY - topY
    if (Math.abs(delta) < 1) return

    for (const branch of branches) {
        for (const nodeId of branch.scopeNodeIds) {
            const pos = positions.get(nodeId)
            if (pos) {
                positions.set(nodeId, { x: pos.x, y: pos.y + delta })
            }
        }
    }
}

// endregion

// region Step 5: Reposition join target

/**
 * Center the join target horizontally on the content midpoint and enforce
 * a minimum vertical gap from the deepest branch node (or split bottom).
 * Uses scopeNodeIds for depth/width computation.
 *
 * Returns `{ yDelta, newJoinY }` where yDelta >= 0 indicates how far
 * the join was pushed down. Mutates `positions` in place.
 */
export function repositionJoinTarget(
    branches: BranchInfo[],
    nodeMap: Map<string, JourneyNode>,
    positions: Map<string, NodePosition>,
    joinStepId: string,
    splitCenterX: number,
    splitPos: NodePosition,
    splitHeight: number,
): JoinRepositionResult {
    const joinNode = nodeMap.get(joinStepId)
    const joinWidth = joinNode?.measured?.width ?? LAYOUT_CONSTANTS.MIN_NODE_WIDTH
    const joinPos = positions.get(joinStepId)

    if (!joinPos) {
        return { yDelta: 0, newJoinY: 0 }
    }

    const splitBottom = splitPos.y + splitHeight
    let deepestY = splitBottom
    for (const branch of branches) {
        for (const nodeId of branch.scopeNodeIds) {
            const pos = positions.get(nodeId)
            const node = nodeMap.get(nodeId)
            const h = node?.measured?.height ?? LAYOUT_CONSTANTS.MIN_NODE_HEIGHT
            if (pos) {
                deepestY = Math.max(deepestY, pos.y + h)
            }
        }
    }

    const allBranchesEmpty = branches.every(b => b.scopeNodeIds.length === 0)
    const minGapFromDeepest = allBranchesEmpty ? 200 : 50
    const minJoinY = deepestY + minGapFromDeepest
    const newJoinY = Math.max(joinPos.y, minJoinY)

    // Center on the horizontal midpoint of all branch content.
    // With adaptive lane widths the content midpoint may differ from splitCenterX.
    let contentMinX = Infinity
    let contentMaxX = -Infinity
    for (const branch of branches) {
        for (const nodeId of branch.scopeNodeIds) {
            const pos = positions.get(nodeId)
            const node = nodeMap.get(nodeId)
            const w = node?.measured?.width ?? LAYOUT_CONSTANTS.MIN_NODE_WIDTH
            if (pos) {
                contentMinX = Math.min(contentMinX, pos.x)
                contentMaxX = Math.max(contentMaxX, pos.x + w)
            }
        }
    }
    const contentCenterX = contentMinX < Infinity
        ? (contentMinX + contentMaxX) / 2
        : splitCenterX

    const yDelta = newJoinY - joinPos.y
    positions.set(joinStepId, { x: contentCenterX - joinWidth / 2, y: newJoinY })

    return { yDelta, newJoinY }
}

// endregion

// region Step 5b: Cascade Y-delta downstream

/**
 * When the join target is pushed down, shift every node reachable from it
 * (excluding split-region nodes) by the same yDelta to preserve ELK's
 * original spacing. Uses scopeNodeIds for region membership.
 * Clears stale edge routing for edges touching shifted nodes.
 *
 * Returns the set of shifted node IDs. Mutates `positions` and `edgeRouting` in place.
 */
export function cascadeDownstreamYDelta(
    yDelta: number,
    joinStepId: string,
    splitNode: JourneyNode,
    branches: BranchInfo[],
    nodeMap: Map<string, JourneyNode>,
    positions: Map<string, NodePosition>,
    edges: JourneyEdge[],
    edgeRouting: Map<string, ElkEdgeSection[]>,
): Set<string> {
    const splitRegionNodeIds = new Set<string>([splitNode.id, joinStepId])
    for (const branch of branches) {
        for (const nid of branch.scopeNodeIds) splitRegionNodeIds.add(nid)
    }

    const shifted = new Set<string>()
    const queue: string[] = [joinStepId]
    while (queue.length > 0) {
        const currentId = queue.shift()!
        const currentNode = nodeMap.get(currentId)
        if (!currentNode) continue

        let successorIds: string[] = []
        if (isBranchingNode(currentNode)) {
            const brData = getBranchingNodeData(currentNode)
            if (brData) {
                successorIds = brData.branches
                    .map(b => b.out_step_id)
                    .filter((id): id is string | number => id != null)
                    .map(String)
            }
        } else {
            const outId = getNodeOutStepId(currentNode)
            if (outId) successorIds = [outId]
        }

        for (const succId of successorIds) {
            if (shifted.has(succId) || splitRegionNodeIds.has(succId)) continue
            shifted.add(succId)
            const succPos = positions.get(succId)
            if (succPos) {
                positions.set(succId, { x: succPos.x, y: succPos.y + yDelta })
            }
            queue.push(succId)
        }
    }

    for (const edge of edges) {
        if (shifted.has(edge.source) || shifted.has(edge.target)) {
            edgeRouting.delete(edge.id)
        }
    }

    return shifted
}

// endregion

// region Clear split-region edge routing

/**
 * Remove stale ELK routing for any edge whose source AND target are both
 * in the split region. Uses scopeNodeIds for region membership.
 */
export function clearSplitRegionEdgeRouting(
    splitNodeId: string,
    joinStepId: string,
    branches: BranchInfo[],
    edges: JourneyEdge[],
    edgeRouting: Map<string, ElkEdgeSection[]>,
): void {
    const splitRegionNodeIds = new Set<string>([splitNodeId, joinStepId])
    for (const branch of branches) {
        for (const nodeId of branch.scopeNodeIds) {
            splitRegionNodeIds.add(nodeId)
        }
    }

    for (const edge of edges) {
        if (splitRegionNodeIds.has(edge.source) && splitRegionNodeIds.has(edge.target)) {
            edgeRouting.delete(edge.id)
        }
    }
}

// endregion

// region Step 6: Update port positions

/**
 * Generate port positions for each variant, mapping lane centers to port X
 * coordinates relative to the split node. Port X is clamped to [10%, 90%]
 * of splitWidth. Port Y equals splitHeight (bottom of split node).
 */
export function updatePortPositions(
    variants: BranchInfo['variant'][],
    laneCenters: number[],
    splitPos: NodePosition,
    splitWidth: number,
    splitHeight: number,
): PortPosition[] {
    return variants.map((v, idx) => {
        const laneCenter = laneCenters[idx] ?? (splitPos.x + splitWidth / 2)
        const relativeX = laneCenter - splitPos.x
        const clampedX = Math.max(splitWidth * 0.1, Math.min(splitWidth * 0.9, relativeX))
        return { portId: `branch-${v.id}`, x: clampedX, y: splitHeight }
    })
}

// endregion

// region Converge context computation

/**
 * Compute the shared converge context used by both split edge and convergence
 * edge section generators. Uses scopeNodeIds for depth computation.
 *
 * The converge Y is a fixed gap above the join target, ensuring consistent
 * vertical spacing regardless of branch depth or nesting. This means every
 * split's edges share the same convergence pattern: horizontal at convergeY,
 * then a fixed-length vertical drop into the join target.
 */
export function computeConvergeContext(
    splitPos: NodePosition,
    splitHeight: number,
    branches: BranchInfo[],
    positions: Map<string, NodePosition>,
    nodeMap: Map<string, JourneyNode>,
    joinStepId: string,
): ConvergeContext {
    const joinNode = nodeMap.get(joinStepId)
    const joinWidth = joinNode?.measured?.width ?? LAYOUT_CONSTANTS.MIN_NODE_WIDTH

    const updatedJoinPos = positions.get(joinStepId)!
    const joinTopCenterX = updatedJoinPos.x + joinWidth / 2
    const joinTopY = updatedJoinPos.y

    const splitBottomY = splitPos.y + splitHeight
    const baseConvergeY = joinTopY - SPLIT_LAYOUT_CONSTANTS.CONVERGE_GAP

    return { baseConvergeY, joinTopCenterX, joinTopY, splitBottomY }
}

// endregion

// region Step 7: Generate split edge sections

/**
 * Generate orthogonal edge sections for split edges (split node → first
 * variant node or join target for empty variants).
 *
 * Uses spineNodeIds[0] for the first target node in a branch (the first
 * node on the linear path, not a nested internal node).
 *
 * Empty variant edges get 4 bend points (port → bendY → lane → convergeY → center).
 * Non-empty variant edges get 0 or 2 bend points depending on alignment.
 * All bend points form orthogonal (Manhattan) paths.
 *
 * Mutates `edgeRouting` in place.
 */
export function generateBranchEdgeSections(
    branches: BranchInfo[],
    ports: PortPosition[],
    splitNode: JourneyNode,
    edges: JourneyEdge[],
    positions: Map<string, NodePosition>,
    nodeMap: Map<string, JourneyNode>,
    laneCenters: number[],
    splitPos: NodePosition,
    convergeCtx: ConvergeContext,
    edgeRouting: Map<string, ElkEdgeSection[]>,
): void {
    const { BEND_GAP } = SPLIT_LAYOUT_CONSTANTS
    const { baseConvergeY, joinTopCenterX, joinTopY } = convergeCtx

    for (let bi = 0; bi < branches.length; bi++) {
        const branch = branches[bi]
        const laneCenterX = laneCenters[bi]
        const port = ports[bi]
        const portAbsX = splitPos.x + port.x
        const portAbsY = splitPos.y + port.y

        const branchEdge = edges.find(e =>
            e.type === 'branch-edge' &&
            e.source === splitNode.id &&
            (e.data as unknown as BranchEdgeData)?.branchKey === branch.variant.id
        )
        if (!branchEdge) continue

        // Use spineNodeIds for first target — this is the first node on the
        // linear path, not a nested internal node.
        const targetNodeId = branch.spineNodeIds.length > 0 ? branch.spineNodeIds[0] : branchEdge.target
        const targetPos = positions.get(targetNodeId)
        if (!targetPos) continue
        const targetNode = nodeMap.get(targetNodeId)
        const targetWidth = targetNode?.measured?.width ?? LAYOUT_CONSTANTS.MIN_NODE_WIDTH
        const targetTopCenterX = targetPos.x + targetWidth / 2
        const targetTopY = targetPos.y

        const bendY = portAbsY + BEND_GAP
        const bendPoints: { x: number; y: number }[] = []
        const targetIsInLane = branch.spineNodeIds.length > 0

        if (targetIsInLane) {
            if (Math.abs(portAbsX - targetTopCenterX) < 8) {
                // Nearly vertically aligned — straight down
            } else {
                bendPoints.push({ x: portAbsX, y: bendY })
                bendPoints.push({ x: targetTopCenterX, y: bendY })
            }
        } else {
            // Empty branch: route from port → lane center → converge level → join target.
            bendPoints.push({ x: portAbsX, y: bendY })
            bendPoints.push({ x: laneCenterX, y: bendY })
            bendPoints.push({ x: laneCenterX, y: baseConvergeY })
            bendPoints.push({ x: joinTopCenterX, y: baseConvergeY })
        }

        const section: ElkEdgeSection = {
            id: `${branchEdge.id}_s0`,
            startPoint: { x: portAbsX, y: portAbsY },
            endPoint: { x: targetTopCenterX, y: targetTopY },
            bendPoints: bendPoints.length > 0 ? bendPoints : undefined,
        }
        edgeRouting.set(branchEdge.id, [section])
    }
}

// endregion

// region Step 8: Generate convergence edge sections

/**
 * Generate orthogonal edge sections for convergence edges (last variant
 * node → join target). Only processes branches with intermediate nodes.
 *
 * Uses spineNodeIds (not scopeNodeIds) for last-node lookup. The last
 * spine node is the actual final node before the join target — not a
 * deeply nested node inside a condition's sub-branch.
 *
 * Mutates `edgeRouting` in place.
 */
export function generateConvergenceEdgeSections(
    branches: BranchInfo[],
    edges: JourneyEdge[],
    positions: Map<string, NodePosition>,
    nodeMap: Map<string, JourneyNode>,
    convergeCtx: ConvergeContext,
    joinStepId: string,
    edgeRouting: Map<string, ElkEdgeSection[]>,
): void {
    const { baseConvergeY, joinTopCenterX, joinTopY } = convergeCtx

    for (let bi = 0; bi < branches.length; bi++) {
        const branch = branches[bi]
        if (branch.spineNodeIds.length === 0) continue

        // Use last spine node — the actual final node before the join target.
        const lastNodeId = branch.spineNodeIds[branch.spineNodeIds.length - 1]
        const convergenceEdge = edges.find(e =>
            e.type !== 'branch-edge' &&
            e.source === lastNodeId &&
            e.target === joinStepId
        )
        if (!convergenceEdge) continue

        const lastNodePos = positions.get(lastNodeId)
        if (!lastNodePos) continue
        const lastNode = nodeMap.get(lastNodeId)
        const lastNodeWidth = lastNode?.measured?.width ?? LAYOUT_CONSTANTS.MIN_NODE_WIDTH
        const lastNodeHeight = lastNode?.measured?.height ?? LAYOUT_CONSTANTS.MIN_NODE_HEIGHT
        const srcCenterX = lastNodePos.x + lastNodeWidth / 2
        const srcBottomY = lastNodePos.y + lastNodeHeight

        const convergeY = baseConvergeY
        const convBendPoints: { x: number; y: number }[] = []

        if (Math.abs(srcCenterX - joinTopCenterX) < 8) {
            // Nearly vertically aligned — straight down (avoid tiny horizontal jogs
            // that create visible "double line" artifacts)
        } else {
            convBendPoints.push({ x: srcCenterX, y: convergeY })
            convBendPoints.push({ x: joinTopCenterX, y: convergeY })
        }

        const convSection: ElkEdgeSection = {
            id: `${convergenceEdge.id}_s0`,
            startPoint: { x: srcCenterX, y: srcBottomY },
            endPoint: { x: Math.abs(srcCenterX - joinTopCenterX) < 8 ? srcCenterX : joinTopCenterX, y: joinTopY },
            bendPoints: convBendPoints.length > 0 ? convBendPoints : undefined,
        }
        edgeRouting.set(convergenceEdge.id, [convSection])
    }
}

// endregion
